import { memo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import useLocalStore from '../../../../hooks/useLocalStore';
import FJMultipleImageUploader from '../../../../common/components/FJMultipleImageUploader/FJMultipleImageUploader';
import FJActionAIPhotoEditor from '../../../../redux/action/FJActionAIPhotoEditor';
import FJActionEditPage from '../../../../redux/action/FJActionEditPage';
import { setSliceIsDraggingFileAction } from '../../../../redux/slices/FJSliceCommon';
import {
    UPLOAD_FILE_TYPE_NO_GIF,
    CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_IMAGES,
    UPLOAD_IMAGE_FILE_TYPE_NO_NEED_TRANSCODE,
} from '../../../../config/dataConfig/FJGenerateData';
import FJImageInput from '../../../../common/components/FJImageInput/FJImageInput';
import FJLocalStore from '../../../../store/FJLocalStore';
import FJTitleLine from '../../../../common/combineComponents/FJTitleLine/FJTitleLine';
import FJConstant from '../../../../constants/FJConstant';

const FJAIPhotoEditorMultipleImageUploaderContainer = memo(function FJAIPhotoEditorMultipleImageUploaderContainer({
    supportImageReferenceNum,
}) {
    useLocalStore();
    const dispatch = useDispatch();

    const inputImages = useSelector(state => state.aiPhotoEditor.inputImages);
    const isDraggingFile = useSelector(state => state.common.isDraggingFile);
    const isLoading = useSelector(state => state.aiPhotoEditor.uploadStatus === FJConstant.LOADING);

    const setIsDraggingFile = useCallback(
        value => {
            dispatch(setSliceIsDraggingFileAction(value));
        },
        [dispatch],
    );

    const handleUploadMultiple = useCallback(
        files => {
            dispatch(FJActionAIPhotoEditor.uploadMultipleImagesAction(files));
        },
        [dispatch],
    );

    const handleRemoveImage = useCallback(
        index => {
            dispatch(FJActionAIPhotoEditor.removeImageFromInputImagesAction(index));
        },
        [dispatch],
    );

    const handleDelete = useCallback(() => {
        dispatch(FJActionAIPhotoEditor.removeImageFromInputImagesAction(0));
    }, [dispatch]);

    const handleOpenMediaLibrary = useCallback(() => {
        dispatch(
            FJActionEditPage.openMediaLibraryAction({
                uploadConfig: {
                    // 上传文件规则
                    isMultiple: supportImageReferenceNum > 1,
                    maxMediaNum: supportImageReferenceNum,
                    selectedMediaIds: inputImages.map(item => item.id),
                    acceptTypeWithTranscode: UPLOAD_FILE_TYPE_NO_GIF + CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_IMAGES,
                    acceptTypeNoTranscode: UPLOAD_IMAGE_FILE_TYPE_NO_NEED_TRANSCODE, // 上传后的校验使用
                    suffixTranscode: CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_IMAGES,
                    // 媒体库的过滤规则
                    mediaFilterFunc: item => {
                        const mediaTypes = ['IMAGE'];
                        return mediaTypes.includes(item.type);
                    },
                    // 上传文件之后返回的数据
                    callbackOptions: {},
                },
            }),
        );
    }, [dispatch, supportImageReferenceNum, inputImages]);

    const onImageUpload = useCallback(
        file => {
            dispatch(FJActionAIPhotoEditor.uploadImageAction(file));
        },
        [dispatch],
    );

    return supportImageReferenceNum > 1 ? (
        <FJMultipleImageUploader
            inputImages={inputImages}
            onUploadMultiple={handleUploadMultiple}
            onRemoveImage={handleRemoveImage}
            isDraggingFile={isDraggingFile}
            setIsDraggingFile={setIsDraggingFile}
            acceptFile={UPLOAD_FILE_TYPE_NO_GIF + CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_IMAGES}
            openMediaLibrary={handleOpenMediaLibrary}
            maxImages={supportImageReferenceNum}
            titleStyle={{ marginTop: '0px', marginBottom: '8px' }}
            isLoading={isLoading}
            isRequire
            previewImageStyle={{ left: '559px' }}
        />
    ) : (
        <>
            <FJTitleLine
                title={FJLocalStore._('upload')}
                isShowHelp={false}
                isShowTry={false}
                style={{ marginTop: '0px', marginBottom: '8px' }}
                isRequire
            />
            <FJImageInput
                uploadImg={onImageUpload}
                uploadImageInfo={inputImages[0] ?? undefined}
                isDraggingFile={isDraggingFile}
                setIsDraggingFile={setIsDraggingFile}
                isDelete
                acceptFile={UPLOAD_FILE_TYPE_NO_GIF + CLOUD_TRANSCODE_SUPPORTED_FILE_TYPE_IMAGES}
                openMediaLibrary={handleOpenMediaLibrary}
                onDelete={handleDelete}
                previewImageStyle={{ left: '559px' }}
            />
        </>
    );
});

FJAIPhotoEditorMultipleImageUploaderContainer.propTypes = {
    supportImageReferenceNum: PropTypes.number,
};

export default FJAIPhotoEditorMultipleImageUploaderContainer;
